/**
 * Weibull Analysis with Right-Censored Data
 * Implements adjusted rank regression using Johnson's method
 * Based on industry standards (IEC 61649, Abernethy's Weibull Handbook)
 */

import { FailureRecord } from '@/types/failure';

export interface CensoredDataPoint {
  time: number;
  isCensored: boolean;
  failureRecord?: FailureRecord;
  adjustedRank?: number;
  medianRank?: number;
}

export interface CensoredWeibullResult {
  parameters: {
    shape: number; // β (beta)
    scale: number; // η (eta)
    rSquared: number;
    shapeCI?: { lower: number; upper: number };
    scaleCI?: { lower: number; upper: number };
  };
  plotData: {
    failures: CensoredDataPoint[];
    censored: CensoredDataPoint[];
    allData: CensoredDataPoint[];
    fittedLine: { x: number; y: number }[];
    confidenceBands?: {
      lower: { x: number; y: number }[];
      upper: { x: number; y: number }[];
    };
  };
  summary: {
    totalSamples: number;
    failureCount: number;
    censoredCount: number;
    censoringPercentage: number;
    meanLife: number;
    medianLife: number;
    characteristicLife: number;
  };
  statistics?: {
    standardErrors: { shape: number; scale: number };
    confidenceLevel: number;
    degreesOfFreedom: number;
  };
}

/**
 * Check if a failure record represents a censored observation
 * Censored = no case_id or failure_id is all zeros
 */
export function isCensoredObservation(record: FailureRecord): boolean {
  const zeroUuid = '00000000-0000-0000-0000-000000000000';

  return (
    !record.case_id ||
    record.case_id === '' ||
    record.case_id === '0' ||
    record.failure_id === zeroUuid
  );
}

/**
 * Calculate adjusted ranks using Johnson's method
 * This properly accounts for censored observations between failures
 */
function calculateAdjustedRanks(sortedData: CensoredDataPoint[]): void {
  const n = sortedData.length;
  let previousAdjustedRank = 0;
  let currentRankIncrement = 0;

  for (let i = 0; i < n; i++) {
    if (!sortedData[i].isCensored) {
      // This is a failure
      const remainingItems = n - i;
      currentRankIncrement = (n + 1 - previousAdjustedRank) / (remainingItems + 1);
      previousAdjustedRank = previousAdjustedRank + currentRankIncrement;
      sortedData[i].adjustedRank = previousAdjustedRank;
    }
    // Censored items don't get ranks
  }
}

/**
 * Calculate median ranks using Benard's approximation with censoring adjustment
 * Only for failure points, using their adjusted ranks
 */
function calculateMedianRanks(
  sortedData: CensoredDataPoint[],
  totalSamples: number,
  censoringPercentage: number
): void {
  sortedData.forEach((point) => {
    if (!point.isCensored && point.adjustedRank) {
      // TEMPORARY FIX: Use more conservative plotting position for high censoring
      // Standard Benard's: (i - 0.3) / (n + 0.4)
      // Conservative: (i - 0.5) / n for > 30% censoring
      if (censoringPercentage > 30) {
        point.medianRank = (point.adjustedRank - 0.5) / totalSamples;
      } else {
        point.medianRank = (point.adjustedRank - 0.3) / (totalSamples + 0.4);
      }
    }
  });
}

/**
 * Get t-critical value for given degrees of freedom and confidence level
 */
function getTCriticalValue(degreesOfFreedom: number, confidenceLevel: number): number {
  // t-distribution critical values lookup table
  const T_CRITICAL_VALUES: Record<number, Record<number, number>> = {
    1: { 0.9: 6.314, 0.95: 12.706, 0.99: 63.657 },
    2: { 0.9: 2.92, 0.95: 4.303, 0.99: 9.925 },
    3: { 0.9: 2.353, 0.95: 3.182, 0.99: 5.841 },
    4: { 0.9: 2.132, 0.95: 2.776, 0.99: 4.604 },
    5: { 0.9: 2.015, 0.95: 2.571, 0.99: 4.032 },
    6: { 0.9: 1.943, 0.95: 2.447, 0.99: 3.707 },
    7: { 0.9: 1.895, 0.95: 2.365, 0.99: 3.499 },
    8: { 0.9: 1.86, 0.95: 2.306, 0.99: 3.355 },
    9: { 0.9: 1.833, 0.95: 2.262, 0.99: 3.25 },
    10: { 0.9: 1.812, 0.95: 2.228, 0.99: 3.169 },
    15: { 0.9: 1.753, 0.95: 2.131, 0.99: 2.947 },
    20: { 0.9: 1.725, 0.95: 2.086, 0.99: 2.845 },
    25: { 0.9: 1.708, 0.95: 2.06, 0.99: 2.787 },
    30: { 0.9: 1.697, 0.95: 2.042, 0.99: 2.75 },
    40: { 0.9: 1.684, 0.95: 2.021, 0.99: 2.704 },
    50: { 0.9: 1.676, 0.95: 2.009, 0.99: 2.678 },
    60: { 0.9: 1.671, 0.95: 2.0, 0.99: 2.66 },
    80: { 0.9: 1.664, 0.95: 1.99, 0.99: 2.639 },
    100: { 0.9: 1.66, 0.95: 1.984, 0.99: 2.626 },
    120: { 0.9: 1.658, 0.95: 1.98, 0.99: 2.617 },
    9999: { 0.9: 1.645, 0.95: 1.96, 0.99: 2.576 }, // Infinity approximation
  };

  // Find the closest degrees of freedom in our table
  const availableDf = Object.keys(T_CRITICAL_VALUES)
    .map(Number)
    .sort((a, b) => a - b);

  let df = degreesOfFreedom;
  if (df > 120) {
    df = 9999; // Use infinity approximation
  } else {
    // Find closest available df
    df = availableDf.reduce((prev, curr) =>
      Math.abs(curr - degreesOfFreedom) < Math.abs(prev - degreesOfFreedom) ? curr : prev
    );
  }

  // Support arbitrary confidence levels by interpolating or using closest match
  let levelKey: number;

  if (confidenceLevel <= 0.9) {
    levelKey = 0.9;
  } else if (confidenceLevel >= 0.99) {
    levelKey = 0.99;
  } else if (Math.abs(confidenceLevel - 0.95) < 0.025) {
    // Close to 95%
    levelKey = 0.95;
  } else if (confidenceLevel < 0.95) {
    // Interpolate between 90% and 95%
    const t90 = T_CRITICAL_VALUES[df][0.9];
    const t95 = T_CRITICAL_VALUES[df][0.95];
    const weight = (confidenceLevel - 0.9) / (0.95 - 0.9);
    return t90 + weight * (t95 - t90);
  } else {
    // Interpolate between 95% and 99%
    const t95 = T_CRITICAL_VALUES[df][0.95];
    const t99 = T_CRITICAL_VALUES[df][0.99];
    const weight = (confidenceLevel - 0.95) / (0.99 - 0.95);
    return t95 + weight * (t99 - t95);
  }

  return T_CRITICAL_VALUES[df][levelKey];
}

/**
 * Simplified gamma function for mean life calculation
 */
function gamma(z: number): number {
  if (z < 0.5) {
    return Math.PI / (Math.sin(Math.PI * z) * gamma(1 - z));
  }
  z -= 1;
  const x =
    0.99999999999980993 +
    676.5203681218851 / (z + 1) +
    -1259.1392167224028 / (z + 2) +
    771.32342877765313 / (z + 3) +
    -176.61502916214059 / (z + 4) +
    12.507343278686905 / (z + 5) +
    -0.13857109526572012 / (z + 6) +
    9.9843695780195716e-6 / (z + 7) +
    1.5056327351493116e-7 / (z + 8);

  const t = z + 7.5;
  return Math.sqrt(2 * Math.PI) * Math.pow(t, z + 0.5) * Math.exp(-t) * x;
}

/**
 * Main function to calculate Weibull parameters with censored data
 */
export function calculateCensoredWeibullParameters(
  failureRecords: FailureRecord[],
  confidenceLevel: number = 0.95
): CensoredWeibullResult {
  // Step 1: Transform data and identify censored observations
  const dataPoints: CensoredDataPoint[] = failureRecords.map((record) => ({
    time: record.operation_time_years,
    isCensored: isCensoredObservation(record),
    failureRecord: record,
  }));

  // Filter out invalid times
  const validData = dataPoints.filter((d) => d.time > 0);

  if (validData.length < 3) {
    throw new Error('Need at least 3 valid data points for Weibull analysis');
  }

  // Step 2: Sort by operation time
  const sortedData = [...validData].sort((a, b) => a.time - b.time);

  // Calculate censoring percentage early for use in adjustments
  const censoredCount = sortedData.filter((d) => d.isCensored).length;
  const totalSamples = sortedData.length;
  const censoringPercentage = (censoredCount / totalSamples) * 100;

  // Step 3: Calculate adjusted ranks using Johnson's method
  calculateAdjustedRanks(sortedData);

  // Step 4: Calculate median ranks for failures only
  calculateMedianRanks(sortedData, totalSamples, censoringPercentage);

  // Step 5: Extract failure data for regression
  const failures = sortedData.filter((d) => !d.isCensored && d.medianRank);

  if (failures.length < 2) {
    throw new Error('Need at least 2 failures for Weibull analysis');
  }

  // Step 6: Perform linear regression on log-log transformed data
  const x = failures.map((f) => Math.log(f.time));
  const y = failures.map((f) => Math.log(-Math.log(1 - f.medianRank!)));

  // Calculate regression parameters
  const n = x.length;
  const sumX = x.reduce((sum, xi) => sum + xi, 0);
  const sumY = y.reduce((sum, yi) => sum + yi, 0);
  const sumXY = x.reduce((sum, xi, i) => sum + xi * y[i], 0);
  const sumXX = x.reduce((sum, xi) => sum + xi * xi, 0);

  const xMean = sumX / n;
  const yMean = sumY / n;

  // Linear regression: y = β*x + c
  const beta = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = yMean - beta * xMean;

  // Weibull parameters
  const shape = beta;
  const scale = Math.exp(-intercept / beta);

  // Calculate R-squared
  const yPredicted = x.map((xi) => beta * xi + intercept);
  const ssRes = y.reduce((sum, yi, i) => sum + Math.pow(yi - yPredicted[i], 2), 0);
  const ssTot = y.reduce((sum, yi) => sum + Math.pow(yi - yMean, 2), 0);
  const rSquared = 1 - ssRes / ssTot;

  // Step 7: Generate fitted line
  const minTime = Math.min(...sortedData.map((d) => d.time));
  const maxTime = Math.max(...sortedData.map((d) => d.time));
  const timeRange = Array.from({ length: 50 }, (_, i) => minTime + (i / 49) * (maxTime - minTime));

  const fittedLine = timeRange.map((t) => ({
    x: t,
    y: 1 - Math.exp(-Math.pow(t / scale, shape)),
  }));

  // Step 8: Calculate confidence intervals (if enough failures)
  let parameterCI;
  let confidenceBands;
  let statistics;

  if (failures.length >= 5) {
    const result = calculateConfidenceIntervals(
      failures,
      x,
      y,
      shape,
      scale,
      confidenceLevel,
      totalSamples,
      censoringPercentage
    );
    parameterCI = result.parameterCI;
    confidenceBands = result.confidenceBands;
    statistics = result.statistics;
  }

  // Step 9: Calculate summary statistics
  const meanLife = scale * gamma(1 + 1 / shape);
  const medianLife = scale * Math.pow(Math.log(2), 1 / shape);
  const characteristicLife = scale;

  const failureCount = failures.length;

  return {
    parameters: {
      shape,
      scale,
      rSquared,
      shapeCI: parameterCI?.shapeCI,
      scaleCI: parameterCI?.scaleCI,
    },
    plotData: {
      failures: failures,
      censored: sortedData.filter((d) => d.isCensored),
      allData: sortedData,
      fittedLine,
      confidenceBands,
    },
    summary: {
      totalSamples,
      failureCount,
      censoredCount,
      censoringPercentage,
      meanLife,
      medianLife,
      characteristicLife,
    },
    statistics,
  };
}

/**
 * Calculate confidence intervals using the Delta method with censoring adjustments
 */
function calculateConfidenceIntervals(
  failures: CensoredDataPoint[],
  x: number[],
  y: number[],
  shape: number,
  scale: number,
  confidenceLevel: number,
  totalSamples: number,
  censoringPercentage: number
): {
  parameterCI: {
    shapeCI: { lower: number; upper: number };
    scaleCI: { lower: number; upper: number };
  };
  confidenceBands: { lower: { x: number; y: number }[]; upper: { x: number; y: number }[] };
  statistics: { standardErrors: { shape: number; scale: number }; degreesOfFreedom: number };
} {
  const n = failures.length;
  // Use effective degrees of freedom based on failure count
  // This provides more accurate t-distribution critical values
  const effectiveFailures = n + (censoringPercentage > 50 ? (totalSamples - n) * 0.2 : 0);
  const degreesOfFreedom = Math.max(effectiveFailures - 2, 3); // Ensure at least 3 DF

  // Calculate residual standard error
  const yPredicted = x.map(
    (xi) =>
      shape * xi + (y.reduce((s, v) => s + v, 0) / n - (shape * x.reduce((s, v) => s + v, 0)) / n)
  );
  const residualSumSquares = y.reduce((sum, yi, i) => sum + Math.pow(yi - yPredicted[i], 2), 0);
  const residualVariance = residualSumSquares / degreesOfFreedom;

  // Apply variance inflation factor based on actual failure count and censoring percentage
  // This approach considers that uncertainty depends more on number of failures than censoring %
  const failureCount = failures.length;
  let varianceInflationFactor: number;

  if (failureCount >= 50) {
    // Excellent failure count - minimal adjustment even with high censoring
    varianceInflationFactor = 1 + (censoringPercentage / 100) * 0.15;
  } else if (failureCount >= 30) {
    // Good failure count - moderate adjustment
    varianceInflationFactor = 1 + (censoringPercentage / 100) * 0.25;
  } else if (failureCount >= 15) {
    // Acceptable failure count - standard adjustment
    varianceInflationFactor = 1 + (censoringPercentage / 100) * 0.35;
  } else {
    // Low failure count - more aggressive adjustment
    varianceInflationFactor = 1 + (censoringPercentage / 100) * 0.5;
  }

  const adjustedResidualVariance = residualVariance * varianceInflationFactor;

  // Standard error of shape parameter
  const xMean = x.reduce((s, v) => s + v, 0) / n;
  const sxx = x.reduce((sum, xi) => sum + Math.pow(xi - xMean, 2), 0);
  const seShape = Math.sqrt(adjustedResidualVariance / sxx);

  // Standard error of scale parameter (using Delta method)
  const seLogScale = Math.sqrt(adjustedResidualVariance * (1 / n + (xMean * xMean) / sxx));
  const seScale = scale * seLogScale;

  // Use proper t-distribution with adjusted degrees of freedom
  const tCritical = getTCriticalValue(degreesOfFreedom, confidenceLevel);

  // Parameter confidence intervals
  const shapeCI = {
    lower: Math.max(0.1, shape - tCritical * seShape),
    upper: shape + tCritical * seShape,
  };

  const scaleCI = {
    lower: Math.max(0.1, scale - tCritical * seScale),
    upper: scale + tCritical * seScale,
  };

  // Confidence bands for the fitted line using a more statistically sound approach
  const times = failures.map((f) => f.time);
  const minTime = Math.min(...times);
  const maxTime = Math.max(...times);
  const timeRange = Array.from({ length: 50 }, (_, i) => minTime + (i / 49) * (maxTime - minTime));

  // Calculate confidence bands using parameter uncertainty propagation
  const confidenceBands = {
    lower: [] as { x: number; y: number }[],
    upper: [] as { x: number; y: number }[],
  };

  for (const t of timeRange) {
    // Calculate point estimate
    const pointF = 1 - Math.exp(-Math.pow(t / scale, shape));

    // For censored data, use conservative parameter bounds approach
    // This is a simplified approach but more robust than the previous implementation
    const fLower = 1 - Math.exp(-Math.pow(t / scaleCI.upper, Math.max(0.1, shapeCI.lower)));
    const fUpper = 1 - Math.exp(-Math.pow(t / scaleCI.lower, shapeCI.upper));

    // Ensure bounds are valid and monotonic
    const finalLower = Math.max(0, Math.min(pointF, fLower, fUpper));
    const finalUpper = Math.min(1, Math.max(pointF, fLower, fUpper));

    confidenceBands.lower.push({ x: t, y: finalLower });
    confidenceBands.upper.push({ x: t, y: finalUpper });
  }

  const lower = confidenceBands.lower;
  const upper = confidenceBands.upper;

  return {
    parameterCI: { shapeCI, scaleCI },
    confidenceBands: { lower, upper },
    statistics: {
      standardErrors: { shape: seShape, scale: seScale },
      degreesOfFreedom,
    },
  };
}

/**
 * Export functions for reliability calculations
 */
export function calculateReliability(time: number, shape: number, scale: number): number {
  return Math.exp(-Math.pow(time / scale, shape));
}

export function calculateFailureRate(time: number, shape: number, scale: number): number {
  return (shape / scale) * Math.pow(time / scale, shape - 1);
}

/**
 * Calculate percentiles for censored data
 */
export function calculatePercentile(percentile: number, shape: number, scale: number): number {
  return scale * Math.pow(-Math.log(1 - percentile / 100), 1 / shape);
}
